/**
 * A character's conversation: the canvas, and what is being said on it.
 *
 * Its own window, and opened by naming a character rather than by finding one
 * standing in the world and clicking it. That was the old way and it was the
 * wrong way round: a conversation belongs to the kind of person, not to one of
 * them standing on a tile, and a character nobody had placed yet had no way to
 * be written for at all.
 */

import { useState } from 'react';
import type { ItemDefinitionWire, NpcDefinitionWire, QuestWire } from '@inverse/protocol';
import type { AdminCommand } from '../Admin';
import { Graph } from './Graph';
import { placeAll, problemsWith } from './graph';
import { Explain } from '../Explain';
import { Conditions, Effects, type DialogueLine } from './Dialogue';
import { Written } from './Written';
import { useSplitter } from './splitter';

export function Conversation({
  npcId,
  npcName,
  lines,
  items,
  quests,
  shops,
  characters,
  objects,
  onEditQuest,
  onLeave,
  onCommand,
}: {
  npcId: number;
  npcName: string;
  lines: readonly DialogueLine[];
  items: readonly ItemDefinitionWire[];
  quests: readonly QuestWire[];
  shops: readonly { id: number; name: string }[];
  characters: readonly NpcDefinitionWire[];
  /** For naming the scenery a deed was done to, in a condition. */
  objects: readonly { id: number; name: string }[];
  /** Opens the quest window, on a named quest when this line is about one. */
  onEditQuest: (quest: string | null) => void;
  /** Lets go of whoever is open, back to the list of everybody in sight. */
  onLeave: () => void;
  onCommand: AdminCommand;
}): JSX.Element {
  const [open, setOpen] = useState<string | null>(lines[0]?.id ?? null);

  /**
   * Where the nodes being dragged are, while they are being dragged.
   *
   * Held here and nowhere else until the drag ends: the canvas has to draw them
   * moving, and the world does not need to hear about every frame of it.
   */
  const [moving, setMoving] = useState<readonly { id: string; x: number; y: number }[]>([]);

  /** Where the canvas is looking, held here so new nodes can land in view. */
  const [view, setView] = useState({ x: 40, y: 40, zoom: 1 });

  /** How much of the window the details take, which is dragged and remembered. */
  const split = useSplitter('conversation', { initial: 272, min: 200, max: 760 });

  /**
   * Takes nodes out, and everything that pointed at them stops pointing.
   *
   * A wire to a node that is gone is not a conversation, it is a dead end
   * nobody asked for. The Start is never taken out this way: a conversation
   * with nothing to open it is a character who has stopped talking.
   */
  const forget = (ids: readonly string[]): void => {
    const going = new Set(
      ids.filter((id) => lines.find((line) => line.id === id)?.kind !== 'start'),
    );
    if (going.size === 0) return;

    const gone = (id: string | undefined): string => (id === undefined || going.has(id) ? '' : id);

    send(
      lines
        .filter((line) => !going.has(line.id))
        .map((line) => ({
          ...line,
          pass: gone(line.pass),
          fail: gone(line.fail),
          next: gone(line.next),
          options: line.options.map((option) => ({ ...option, goto: gone(option.goto) })),
        })),
    );

    if (open !== null && going.has(open)) setOpen(null);
  };

  // Once the conversation comes back saying they are where they were dropped,
  // the local copy has nothing left to say and gets out of the way.
  if (
    moving.length > 0 &&
    moving.every((one) => {
      const held = lines.find((line) => line.id === one.id);
      return held?.at && held.at.x === one.x && held.at.y === one.y;
    })
  ) {
    setMoving([]);
  }
  const found = lines.find((line) => line.id === open) ?? null;

  /**
   * The last node that was open, for the moment a rename is in flight.
   *
   * Renaming points the panel at a name the world has not heard of yet, and for
   * the fraction of a second the save takes there is no such node. Blanking the
   * inspector under somebody's hands while they are still typing in it is worse
   * than showing them what they were just editing.
   */
  const [lastHeld, setLastHeld] = useState<DialogueLine | null>(null);
  if (found !== null && found !== lastHeld) setLastHeld(found);

  const held = found ?? (open !== null ? lastHeld : null);
  const problems = problemsWith(lines);

  /** The whole conversation goes over at once: it is small, and it is one edit. */
  const send = (next: DialogueLine[]): void => {
    onCommand('setDialogue', { npc: npcId }, { dialogue: JSON.stringify(next) });
  };

  const change = (id: string, patch: Partial<DialogueLine>): void => {
    send(lines.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  };

  const freshId = (stem: string): string => {
    let at = lines.length + 1;
    while (lines.some((line) => line.id === `${stem}${at}`)) at++;
    return `${stem}${at}`;
  };

  /** Somewhere clear to drop a new one, rather than on top of the last. */
  /**
   * Somewhere clear to put a new node, in the part of the canvas being looked at.
   *
   * It used to go off the right hand end of the whole tree, which is off the
   * screen as soon as a conversation is more than a few lines: adding a node
   * meant adding it and then going to find it.
   */
  const spareSpot = (): { x: number; y: number } => {
    const placed = [...placeAll(lines).values()];

    // The canvas in its own numbers. Roughly, since what matters is landing in
    // sight rather than to the pixel.
    const seen = {
      x: -view.x / view.zoom,
      y: -view.y / view.zoom,
      width: 900 / view.zoom,
      height: 620 / view.zoom,
    };

    const clear = (x: number, y: number): boolean =>
      placed.every((one) => Math.abs(one.x - x) > 210 || Math.abs(one.y - y) > 110);

    // Down the visible area and across it, taking the first gap. A conversation
    // dense enough to have no gap at all gets the middle, on top of whatever is
    // there, which is at least somewhere it can be seen and dragged off.
    for (let down = 0; down < Math.max(1, Math.floor(seen.height / 130)); down++) {
      for (let across = 0; across < Math.max(1, Math.floor(seen.width / 260)); across++) {
        const x = Math.round(seen.x + 40 + across * 260);
        const y = Math.round(seen.y + 40 + down * 130);

        if (clear(x, y)) return { x, y };
      }
    }

    return { x: Math.round(seen.x + seen.width / 2), y: Math.round(seen.y + seen.height / 2) };
  };

  const start = lines.find((line) => line.kind === 'start') ?? null;

  /** A blank node of a kind, with a name nothing else is using. */
  const blank = (
    kind: NonNullable<DialogueLine['kind']>,
    at: { x: number; y: number },
  ): DialogueLine => {
    const stem =
      kind === 'branch'
        ? 'check'
        : kind === 'directory'
          ? 'ask'
          : kind === 'priority'
            ? 'instead'
            : kind === 'note'
              ? 'note'
              : kind === 'checkpoint'
                ? 'back'
                : 'line';

    return {
      id: freshId(stem),
      kind,
      text: kind === 'line' ? 'Well met.' : '',
      when: [],
      does: [],
      pass: '',
      fail: '',
      next: '',
      options: [],
      at,
    };
  };

  const add = (kind: NonNullable<DialogueLine['kind']>): void => {
    const made = blank(kind, spareSpot());

    // The first thing put down after the start is wired to it, since that is
    // what somebody meant by putting it down.
    const next =
      start && (start.next ?? '').length === 0 && kind !== 'note' && kind !== 'priority'
        ? lines.map((line) => (line.id === start.id ? { ...line, next: made.id } : line))
        : lines;

    send([...next, made]);
    setOpen(made.id);
  };

  /**
   * A node put down where a wire was let go of, already wired to it.
   *
   * The same arithmetic the canvas uses to decide where a wire goes, since what
   * was just drawn is exactly a wire: it simply arrives at something that did
   * not exist a moment ago.
   */
  const createFrom = (
    from: string,
    way: number,
    kind: NonNullable<DialogueLine['kind']>,
    at: { x: number; y: number },
  ): void => {
    const node = lines.find((line) => line.id === from);
    if (!node) return;

    const made = blank(kind, { x: Math.round(at.x), y: Math.round(at.y - 20) });

    const wired = lines.map((line) => {
      if (line.id !== from) return line;

      if (line.kind === 'branch') {
        return way === 0 ? { ...line, pass: made.id } : { ...line, fail: made.id };
      }

      if (
        line.kind === 'start' ||
        line.kind === 'priority' ||
        line.kind === 'checkpoint' ||
        line.options.length === 0 ||
        way >= line.options.length
      ) {
        return { ...line, next: made.id };
      }

      return {
        ...line,
        options: line.options.map((option, index) =>
          index === way ? { ...option, goto: made.id } : option,
        ),
      };
    });

    send([...wired, made]);
    setOpen(made.id);
  };

  /** The quest this line is about, if it moves one along or asks after one. */
  const aboutQuest = ((): string | null => {
    if (!held) return null;

    for (const effect of held.does) if (effect.quest) return effect.quest;
    for (const condition of held.when) if (condition.quest) return condition.quest;

    for (const option of held.options) {
      for (const effect of option.does) if (effect.quest) return effect.quest;
      for (const condition of option.when) if (condition.quest) return condition.quest;
    }

    return null;
  })();

  return (
    <div className="conversation">
      <div className="conversation-bar">
        {/* The way back to the list of everybody in sight. Without it, opening
            somebody was a one way door: the only way to write for anybody else
            was to close the window and open it again. */}
        <button
          type="button"
          className="editor-tab"
          title="Back to everybody in sight"
          onClick={onLeave}
        >
          Back
        </button>
        <span className="conversation-who">{npcName}</span>

        <button type="button" className="editor-tab" onClick={() => add('line')}>
          Line
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Directory: a list of topics with no line of its own"
          onClick={() => add('directory')}
        >
          Directory
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Says nothing. Looks at its conditions and picks one of two ways."
          onClick={() => add('branch')}
        >
          Branch
        </button>
        <button
          type="button"
          className="editor-tab"
          title="A start of its own. When its conditions hold, its tree runs instead of the ordinary one."
          onClick={() => add('priority')}
        >
          Priority
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Where the talk comes back to next time somebody speaks to them"
          onClick={() => add('checkpoint')}
        >
          Checkpoint
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Note: never shown in game, for the author"
          onClick={() => add('note')}
        >
          Note
        </button>

        {start === null ? (
          <button
            type="button"
            className="editor-tab warning"
            title="No Start node, so nothing opens this conversation"
            onClick={() => add('start')}
          >
            Add a Start
          </button>
        ) : null}

        <button
          type="button"
          className="editor-tab"
          disabled={!held}
          title="Preview the conversation from the open node"
          onClick={() => held && onCommand('sayLine', { npc: npcId }, { line: held.id })}
        >
          Preview
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Forget every line of theirs already heard, so a first-time line is said again"
          onClick={() => onCommand('forgetTalk', { npc: npcId })}
        >
          Reset Progress
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Auto-arrange the graph"
          onClick={() => send(lines.map((line) => ({ ...line, at: undefined })))}
        >
          Auto-Arrange
        </button>
        <button
          type="button"
          className="editor-tab"
          title={aboutQuest ? `Edit ${aboutQuest}` : 'Open the quests window'}
          onClick={() => onEditQuest(aboutQuest)}
        >
          {aboutQuest ? 'Edit Quest' : 'Quests'}
        </button>
      </div>

      <div className="conversation-body">
        <Graph
          nodes={lines.map((line) => {
            const held = moving.find((one) => one.id === line.id);
            const at = held ? { x: held.x, y: held.y } : line.at;

            // Anything that opens a window ends the talk, so the canvas draws it
            // without a way out: there is nothing after it to wire to.
            const stopping = (does: readonly { opensMenu?: string }[] | undefined): boolean =>
              (does ?? []).some((one) => one.opensMenu !== undefined);

            return {
              ...line,
              at,
              ends: stopping(line.does),
              options: line.options.map((option) => ({
                ...option,
                ends: stopping(option.does),
              })),
            };
          })}
          open={open}
          view={view}
          onView={setView}
          onCreate={createFrom}
          onOpen={setOpen}
          onMove={(moves) => setMoving(moves)}
          onMoved={(moves) => {
            // Kept where they were dropped until the world says the same thing
            // back. Letting go first meant a frame or two drawn at the old
            // position while the save was in flight, which is the flash.
            setMoving(moves);

            // All of them in one write, so dragging six nodes is one thing to
            // take back rather than six.
            send(
              lines.map((line) => {
                const held = moves.find((one) => one.id === line.id);
                return held ? { ...line, at: { x: held.x, y: held.y } } : line;
              }),
            );
          }}
          onConnect={(from, way, to) => {
            const node = lines.find((line) => line.id === from);
            if (!node) return;

            // A node cannot lead to itself. The conversation would say the same
            // line forever, and the canvas would draw a wire leaving a box and
            // arriving back at it, which reads as a mistake because it is one.
            if (from === to) return;

            if (node.kind === 'branch') {
              change(from, way === 0 ? { pass: to } : { fail: to });
              return;
            }

            // A start, a priority, and a line with no answers all have one way
            // out, which is written down as the one way out. So does the extra
            // way a once-only line carries under its answers.
            if (
              node.kind === 'start' ||
              node.kind === 'priority' ||
              node.options.length === 0 ||
              way >= node.options.length
            ) {
              change(from, { next: to });
              return;
            }

            change(from, {
              options: node.options.map((option, at) =>
                at === way ? { ...option, goto: to } : option,
              ),
            });
          }}
          onForget={forget}
          onCarry={(id, event) => {
            event.dataTransfer.effectAllowed = 'copy';
            event.dataTransfer.setData('text/plain', id);
            event.dataTransfer.setData('application/inverse-line', id);
            event.dataTransfer.setData('application/inverse-npc', String(npcId));
          }}
        />

        {/* Dragged to give the details more room, or to give it back to the
            canvas. Double click puts it where it started. */}
        <div
          className={split.dragging ? 'conversation-grip on' : 'conversation-grip'}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the details"
          title="Drag to resize, double click to reset"
          onPointerDown={split.onPointerDown}
          onDoubleClick={split.reset}
        />

        <div className="conversation-side" style={{ flexBasis: `${split.width}px` }}>
          {problems.length > 0 ? (
            <ul className="tree-problems">
              {problems.slice(0, 5).map((said) => (
                <li key={said}>{said}</li>
              ))}
            </ul>
          ) : null}

          {held ? (
            <>
              <div className="editor-line-head">
                <span className="editor-line-head-name">{held.id}</span>
                {held.kind === 'start' ? null : (
                  <button
                    type="button"
                    className="editor-tab"
                    title="Duplicate this node"
                    onClick={() => {
                      const copy = freshId(held.kind === 'branch' ? 'check' : 'line');
                      const at = held.at ?? { x: 0, y: 0 };

                      send([...lines, { ...held, id: copy, at: { x: at.x + 30, y: at.y + 60 } }]);
                      setOpen(copy);
                    }}
                  >
                    Copy
                  </button>
                )}
                <button
                  type="button"
                  className="editor-tab"
                  title="Delete this node"
                  onClick={() => {
                    // Everything that pointed here stops pointing here. A wire
                    // to a node that is gone is not a conversation, it is a
                    // dead end nobody asked for.
                    forget([held.id]);
                  }}
                >
                  ✕
                </button>
              </div>

              <label className="editor-field">
                <span>Name</span>
                <Written
                  key={`id-${held.id}`}
                  value={held.id}
                  limit={48}
                  // Not on a pause: renaming rewrites every wire that pointed
                  // at the old name, and doing that mid-word is doing it to a
                  // name nobody meant.
                  onlyOnLeaving
                  clean={(text) => text.replace(/[^a-zA-Z0-9_-]/g, '')}
                  onDone={(id) => {
                    if (id.length === 0 || id === held.id) return;

                    // A name already in use is refused rather than taken. Two
                    // nodes under one name is a conversation nobody can wire
                    // up, and it is also how a node came to lead to itself: the
                    // wires that pointed at the old name followed it home.
                    if (lines.some((line) => line.id === id)) return;

                    // Everything pointing at the old name follows it, or renaming
                    // a line quietly breaks every answer that went there.
                    send(
                      lines.map((line) => ({
                        ...line,
                        id: line.id === held.id ? id : line.id,
                        pass: line.pass === held.id ? id : line.pass,
                        fail: line.fail === held.id ? id : line.fail,
                        next: line.next === held.id ? id : line.next,
                        options: line.options.map((option) =>
                          option.goto === held.id ? { ...option, goto: id } : option,
                        ),
                      })),
                    );
                    setOpen(id);
                  }}
                />
              </label>

              {held.kind === 'start' ? (
                <p className="hint">
                  Where the conversation begins. Pull its pin onto whichever node it should open at.
                  Nothing else depends on it and nothing leads into it.
                </p>
              ) : null}

              {held.kind === 'checkpoint' ? (
                <>
                  <p className="hint">
                    Where the talk comes back to.
                    <Explain>
                      Once somebody has been through here, the next time they speak to this
                      character the talk begins here rather than at the top. Remembered against the
                      way in it was reached by, so a checkpoint under a tree that has stopped taking
                      over says nothing about the ordinary conversation.
                    </Explain>
                  </p>

                  <label className="editor-field">
                    <span>
                      Ends the talk here
                      <Explain>
                        For the first time through only. Asking somebody to fetch three logs and
                        then telling them in the same breath that they have not brought any is not a
                        conversation. Coming back, it carries straight on.
                      </Explain>
                    </span>
                    <input
                      type="checkbox"
                      checked={held.stops === true}
                      onChange={(event) => change(held.id, { stops: event.target.checked })}
                    />
                  </label>

                  <h4>Conditions</h4>
                  <p className="hint">
                    Left empty it holds for as long as its way in does.
                    <Explain>
                      Given conditions, the talk only comes back here while they hold. When they
                      stop, it begins at the top of that way in again.
                    </Explain>
                  </p>
                  <Conditions
                    held={held.when}
                    items={items}
                    quests={quests}
                    characters={characters}
                    objects={objects}
                    onChange={(when) => change(held.id, { when })}
                  />
                </>
              ) : null}

              {held.kind === 'note' ? (
                <label className="editor-field">
                  <span>Note</span>
                  <Written
                    key={`note-${held.id}`}
                    rows={4}
                    value={held.text}
                    onDone={(text) => change(held.id, { text })}
                  />
                </label>
              ) : null}

              {held.kind === 'priority' ? (
                <>
                  <p className="hint">
                    When these hold, this tree runs instead of the ordinary one. Heaviest is looked
                    at first.
                  </p>
                  <h4>Conditions</h4>
                  <Conditions
                    held={held.when}
                    items={items}
                    quests={quests}
                    characters={characters}
                    objects={objects}
                    onChange={(when) => change(held.id, { when })}
                  />
                  <label className="editor-field">
                    <span>Weight</span>
                    <Written
                      key={`weight-${held.id}`}
                      value={String(held.weight ?? 0)}
                      limit={3}
                      clean={(text) => text.replace(/[^0-9]/g, '')}
                      onDone={(weight) =>
                        change(held.id, { weight: Math.max(0, Math.round(Number(weight)) || 0) })
                      }
                    />
                  </label>
                </>
              ) : null}

              {held.kind === 'branch' ? (
                <>
                  <h4>Conditions</h4>
                  <Conditions
                    held={held.when}
                    items={items}
                    quests={quests}
                    characters={characters}
                    objects={objects}
                    onChange={(when) => change(held.id, { when })}
                  />
                  <p className="hint">Pull the True and False pins to say where each goes.</p>
                </>
              ) : null}

              {held.kind === 'line' || held.kind === 'directory' ? (
                <>
                  {held.kind === 'line' ? (
                    <>
                      <label className="editor-field">
                        <span>Text</span>
                        <Written
                          key={`says-${held.id}`}
                          rows={3}
                          value={held.text}
                          onDone={(text) => change(held.id, { text })}
                        />
                      </label>

                      <label className="editor-field">
                        <span>
                          Only once
                          <Explain>
                            Said the first time somebody meets this character and never again. After
                            that the conversation goes straight on to whatever follows it.
                          </Explain>
                        </span>
                        <input
                          type="checkbox"
                          checked={held.once === true}
                          onChange={(event) => change(held.id, { once: event.target.checked })}
                        />
                      </label>
                    </>
                  ) : (
                    <p className="hint">
                      Nothing is said. The choices below are offered on their own, which is how one
                      character carries several conversations.
                    </p>
                  )}

                  <h4>Conditions</h4>
                  <Conditions
                    held={held.when}
                    items={items}
                    quests={quests}
                    characters={characters}
                    objects={objects}
                    onChange={(when) => change(held.id, { when })}
                  />

                  <h4>Effects</h4>
                  <Effects
                    shops={shops}
                    characters={characters}
                    held={held.does}
                    items={items}
                    quests={quests}
                    onChange={(does) => change(held.id, { does })}
                  />

                  <h4>{held.kind === 'directory' ? 'Choices' : 'Answers'}</h4>
                  {held.options.map((option, at) => (
                    <div className="editor-answer" key={at}>
                      <label className="editor-field">
                        <span>{held.kind === 'directory' ? 'Choice' : 'Answer'}</span>
                        <Written
                          key={`answer-${held.id}-${at}`}
                          value={option.text}
                          limit={120}
                          onDone={(text) =>
                            change(held.id, {
                              options: held.options.map((one, index) =>
                                index === at ? { ...one, text } : one,
                              ),
                            })
                          }
                        />
                      </label>

                      <h5>Conditions</h5>
                      <Conditions
                        held={option.when}
                        items={items}
                        quests={quests}
                        characters={characters}
                        objects={objects}
                        onChange={(when) =>
                          change(held.id, {
                            options: held.options.map((one, index) =>
                              index === at ? { ...one, when } : one,
                            ),
                          })
                        }
                      />

                      {option.when.length > 0 ? (
                        <div className="tool-row">
                          <span className="tool-label">When Unmet</span>
                          {(
                            [
                              ['hide', 'Hidden'],
                              ['disable', 'Greyed Out'],
                            ] as const
                          ).map(([how, label]) => (
                            <button
                              key={how}
                              type="button"
                              className={
                                (option.ifUnmet ?? 'hide') === how ? 'editor-tab on' : 'editor-tab'
                              }
                              onClick={() =>
                                change(held.id, {
                                  options: held.options.map((one, index) =>
                                    index === at ? { ...one, ifUnmet: how } : one,
                                  ),
                                })
                              }
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      ) : null}

                      <h5>Effects</h5>
                      <Effects
                        shops={shops}
                        characters={characters}
                        held={option.does}
                        items={items}
                        quests={quests}
                        onChange={(does) =>
                          change(held.id, {
                            options: held.options.map((one, index) =>
                              index === at ? { ...one, does } : one,
                            ),
                          })
                        }
                      />

                      <button
                        type="button"
                        className="editor-tab"
                        onClick={() =>
                          change(held.id, {
                            options: held.options.filter((_, index) => index !== at),
                          })
                        }
                      >
                        Take this one out
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="wide"
                    onClick={() =>
                      change(held.id, {
                        options: [
                          ...held.options,
                          { text: 'Go on.', goto: '', when: [], does: [], ifUnmet: 'hide' },
                        ],
                      })
                    }
                  >
                    Add {held.kind === 'directory' ? 'Topic' : 'Answer'}
                  </button>

                  {held.kind === 'line' && held.options.length === 0 ? (
                    <p className="hint">
                      With no answers written, the player is shown a Continue and the conversation
                      goes wherever the one pin leads.
                    </p>
                  ) : null}
                </>
              ) : null}
            </>
          ) : (
            <p className="hint">Press a line on the canvas to write it.</p>
          )}
        </div>
      </div>
    </div>
  );
}
